import React, { useMemo } from "react";
import styled from "styled-components";
import type { CategoryDocument, CategoryHierarchy, HierarchyNode } from "../types/Category.ts";
import Breadcrumb from "./Breadcrumb.tsx";
import StyledButton from "./Button.tsx";
import { LucideEdit3Icon, LucideTrashIcon } from "../icons/lucideBundled.tsx";
import { FluentEmojiFileFolderIcon } from "../icons/fluentEmojiFlatBundled.tsx";
import { VscodeFileTypePdf2Icon } from "../icons/vscodeIconsBundled.tsx";

const FolderContainer = styled.div`
  margin-bottom: 1rem;
`;

const FolderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.75rem 0.75rem 0.75rem;
  background: #f5f5f5;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  width: 100%;
  min-width: 0;

  .dark-mode & {
    background: #3a3a4e;
  }

  &:hover {
    background: #e8e8e8;
  }

  .dark-mode &:hover {
    background: #4a4a5e;
  }
`;

const FolderMain = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
`;

const FolderIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  margin-top: 0.1rem;
  flex-shrink: 0;

  svg {
    display: block;
  }
`;

const FolderTextCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  gap: 0.2rem;
`;

const FolderNameText = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: #001f52;
  line-height: 1.25;
  width: 100%;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const FolderDescriptionText = styled.span`
  display: block;
  width: 100%;
  font-size: 0.8rem;
  font-weight: 400;
  line-height: 1.35;
  min-height: 1.35em;
  color: #666;
  word-break: break-word;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

const PlacementText = styled(FolderDescriptionText)`
  margin-top: 0.15rem;
`;

const FolderActions = styled.div<{ $alignTop?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;

  ${({ $alignTop }) =>
    $alignTop &&
    `
    align-self: flex-start;
    margin-top: 0.1rem;
  `}
`;

const FolderIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 31, 82, 0.08);
  }

  .dark-mode & {
    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }

  svg {
    width: 22px;
    height: 22px;
    display: block;
    color: #001f52;
  }

  .dark-mode & svg {
    color: #e0e0e0;
  }
`;

const DocsListBlock = styled.div`
  margin-top: 1rem;
`;

const DocIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: transparent;
  line-height: 1;
  margin-top: 0.1rem;
  flex-shrink: 0;

  svg {
    display: block;
  }
`;

const EmptyMessage = styled.div`
  color: #666;
  font-size: 0.9rem;
  padding: 1rem;
  text-align: center;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 1.5rem;
`;

interface DocumentFolderViewProps {
  documents: CategoryDocument[];
  documentsLoading?: boolean;
  hierarchy: CategoryHierarchy;
  onDocumentClick: (doc: CategoryDocument) => void;
  searchQuery?: string;
  currentPath?: string[];
  onPathChange?: (path: string[]) => void;
  categoryName?: string;
  /** When `standard`, folder creation is disabled (read-only tree). */
  categoryType?: "standard" | "custom";
  onAddFolder?: (parentNodeId: number, parentFolderName?: string) => void;
  /** Custom categories: open editor for a folder (hierarchy node). */
  onEditFolder?: (args: { nodeId: number; name: string; description: string }) => void;
  /** Custom categories: open editor for a document (PUT document). */
  onEditDocument?: (args: {
    documentId: number;
    nodeId: number;
    name: string;
    description: string;
  }) => void;
  /** Custom categories: open delete confirmation for a document. */
  onDeleteDocument?: (args: {
    documentId: number;
    nodeId: number;
    name: string;
  }) => void;
  /** Custom categories: open delete confirmation for a folder. */
  onDeleteFolder?: (args: {
    nodeId: number;
    name: string;
  }) => void;
}

interface FolderItemData {
  id: number;
  name: string;
  description?: string;
  path: string[];
}

const getNodeSegment = (nodeId: number) => `node-${nodeId}`;
const getPathKey = (path: string[]) => (path.length > 0 ? path.join("/") : "root");

const normalizeHierarchyPath = (path?: string) =>
  (path || "")
    .split(">")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" > ");

export default function DocumentFolderView({
  documents,
  documentsLoading = false,
  hierarchy,
  onDocumentClick,
  searchQuery = "",
  currentPath = [],
  onPathChange,
  categoryName,
  categoryType = "custom",
  onAddFolder,
  onEditFolder,
  onEditDocument,
  onDeleteDocument,
  onDeleteFolder,
}: DocumentFolderViewProps) {
  const hierarchyIndex = useMemo(() => {
    const nodeByPath = new Map<string, HierarchyNode>();
    const pathByNodeId = new Map<number, string[]>();
    const namePathByNodeId = new Map<number, string[]>();
    const pathByNamePath = new Map<string, string[]>();

    pathByNodeId.set(hierarchy.id, []);
    namePathByNodeId.set(hierarchy.id, []);

    const visit = (node: HierarchyNode, parentPath: string[], parentNames: string[]) => {
      const path = [...parentPath, getNodeSegment(node.id)];
      const pathKey = getPathKey(path);
      const nameParts = [...parentNames, node.name];
      const normalizedNamePath = normalizeHierarchyPath(nameParts.join(" > "));

      nodeByPath.set(pathKey, node);
      pathByNodeId.set(node.id, path);
      namePathByNodeId.set(node.id, nameParts);
      if (normalizedNamePath) {
        pathByNamePath.set(normalizedNamePath, path);
      }

      (node.children || []).forEach((child) => visit(child, path, nameParts));
    };

    (hierarchy.children || []).forEach((child) => visit(child, [], []));

    return {
      nodeByPath,
      pathByNodeId,
      namePathByNodeId,
      pathByNamePath,
    };
  }, [hierarchy]);

  const documentsByPath = useMemo(() => {
    const map = new Map<string, CategoryDocument[]>();

    documents.forEach((doc) => {
      let resolvedPath: string[] | undefined;

      if (doc.nodeId) {
        resolvedPath = hierarchyIndex.pathByNodeId.get(doc.nodeId);
      }

      if (!resolvedPath && doc.hierarchyPath) {
        resolvedPath = hierarchyIndex.pathByNamePath.get(normalizeHierarchyPath(doc.hierarchyPath));
      }

      const pathKey = getPathKey(resolvedPath || []);
      if (!map.has(pathKey)) {
        map.set(pathKey, []);
      }
      map.get(pathKey)!.push(doc);
    });

    return map;
  }, [documents, hierarchyIndex]);

  const currentPathKey = getPathKey(currentPath);
  const currentNode = currentPath.length === 0 ? hierarchy : hierarchyIndex.nodeByPath.get(currentPathKey);
  const currentChildren = useMemo(
    () =>
      currentPath.length === 0 ? (hierarchy.children || []) : (currentNode?.children || []),
    [currentPath.length, hierarchy.children, currentNode?.children]
  );
  const currentDocuments = documentsByPath.get(currentPathKey) || [];

  const currentFolderItems = useMemo<FolderItemData[]>(() => {
    return currentChildren.map((node) => {
      const path = [...currentPath, getNodeSegment(node.id)];
      return {
        id: node.id,
        name: node.name,
        description: node.description,
        path,
      };
    });
  }, [currentChildren, currentPath]);

  const breadcrumbItems = useMemo(() => {
    return currentPath
      .map((_, index) => {
        const path = currentPath.slice(0, index + 1);
        const node = hierarchyIndex.nodeByPath.get(getPathKey(path));
        if (!node) {
          return null;
        }
        return {
          label: node.name,
          path,
        };
      })
      .filter((item): item is { label: string; path: string[] } => Boolean(item));
  }, [currentPath, hierarchyIndex]);

  const handleFolderClick = (path: string[]) => {
    onPathChange?.(path);
  };

  const hasSearch = searchQuery.trim().length > 0;
  const isStandardCategory = categoryType === "standard";
  const canAddFolder =
    !isStandardCategory && !hasSearch && Boolean(currentNode) && currentDocuments.length === 0;

  const resolveNodeIdForDocument = (doc: CategoryDocument): number | undefined => {
    if (doc.nodeId != null) return doc.nodeId;
    if (doc.hierarchyPath) {
      const resolvedPath = hierarchyIndex.pathByNamePath.get(normalizeHierarchyPath(doc.hierarchyPath));
      if (resolvedPath) {
        const pathKey = getPathKey(resolvedPath);
        return hierarchyIndex.nodeByPath.get(pathKey)?.id;
      }
    }
    return currentNode?.id;
  };

  const resolveNodeIdForPlacement = (doc: CategoryDocument): number | undefined => {
    if (doc.nodeId != null) return doc.nodeId;
    if (doc.hierarchyPath) {
      const resolvedPath = hierarchyIndex.pathByNamePath.get(normalizeHierarchyPath(doc.hierarchyPath));
      if (resolvedPath) {
        const pathKey = getPathKey(resolvedPath);
        return hierarchyIndex.nodeByPath.get(pathKey)?.id;
      }
    }
    return undefined;
  };

  const getDocumentPlacement = (doc: CategoryDocument): string | null => {
    const folderParts: string[] = [];

    const nodeId = resolveNodeIdForPlacement(doc);
    if (nodeId != null) {
      const folderNames = hierarchyIndex.namePathByNodeId.get(nodeId);
      if (folderNames?.length) {
        folderParts.push(...folderNames);
      }
    } else if (doc.hierarchyPath) {
      const pathParts = normalizeHierarchyPath(doc.hierarchyPath)
        .split(" > ")
        .map((part) => part.trim())
        .filter(Boolean);
      if (pathParts.length > 0) {
        folderParts.push(...pathParts);
      }
    }

    if (folderParts.length === 0) {
      return null;
    }

    const categoryLabel = categoryName?.trim();
    const parts = categoryLabel ? [categoryLabel, ...folderParts] : folderParts;
    return parts.join(" > ");
  };

  const renderDocumentList = (docs: CategoryDocument[], showPlacement = false) =>
    docs.map((doc) => {
      const secondaryRaw = doc.description?.trim() || (!showPlacement ? doc.hierarchyPath : "") || "";
      const secondary = secondaryRaw.trim();
      const placement = showPlacement ? getDocumentPlacement(doc) : null;

      return (
        <FolderContainer key={doc.id}>
          <FolderRow>
            <FolderMain type="button" onClick={() => onDocumentClick(doc)}>
              <DocIcon aria-hidden>
                <VscodeFileTypePdf2Icon size={36} />
              </DocIcon>
              <FolderTextCol>
                <FolderNameText>{doc.name}</FolderNameText>
                <FolderDescriptionText>{secondary || "\u00a0"}</FolderDescriptionText>
                {showPlacement && (
                  <PlacementText>
                    {placement ? `Placering: ${placement}` : "Placering ukendt"}
                  </PlacementText>
                )}
              </FolderTextCol>
            </FolderMain>
            {categoryType === "custom" && (
              <FolderActions $alignTop={showPlacement}>
                <FolderIconButton
                  type="button"
                  title="Rediger dokument"
                  aria-label="Rediger dokument"
                  onClick={(e) => {
                    e.stopPropagation();
                    const nodeId = resolveNodeIdForDocument(doc);
                    if (nodeId == null) {
                      console.warn("Cannot edit document: could not resolve hierarchy node id", doc.id);
                      return;
                    }
                    onEditDocument?.({
                      documentId: doc.id,
                      nodeId,
                      name: doc.name,
                      description: doc.description?.trim() ?? "",
                    });
                  }}
                >
                  <LucideEdit3Icon />
                </FolderIconButton>
                <FolderIconButton
                  type="button"
                  title="Slet dokument"
                  aria-label="Slet dokument"
                  onClick={(e) => {
                    e.stopPropagation();
                    const nodeId = resolveNodeIdForDocument(doc);
                    if (nodeId == null) {
                      console.warn("Cannot delete document: could not resolve hierarchy node id", doc.id);
                      return;
                    }
                    onDeleteDocument?.({
                      documentId: doc.id,
                      nodeId,
                      name: doc.name,
                    });
                  }}
                >
                  <LucideTrashIcon />
                </FolderIconButton>
              </FolderActions>
            )}
          </FolderRow>
        </FolderContainer>
      );
    });

  return (
    <div>
      {onPathChange && (
        <Breadcrumb
          items={breadcrumbItems}
          onNavigate={handleFolderClick}
          categoryName={categoryName}
        />
      )}

      {hasSearch && documentsLoading && (
        <EmptyMessage>Indlæser indhold...</EmptyMessage>
      )}

      {hasSearch && !documentsLoading && documents.length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{
            fontSize: "0.9rem",
            fontWeight: 600,
            color: "#001f52",
            marginBottom: "0.75rem",
            transition: "color 0.3s ease"
          }} className="dark-mode-text-primary">
            Søgeresultater ({documents.length})
          </div>
          <DocsListBlock>{renderDocumentList(documents, true)}</DocsListBlock>
        </div>
      )}

      {hasSearch && !documentsLoading && documents.length === 0 && (
        <EmptyMessage>Ingen dokumenter matcher &quot;{searchQuery.trim()}&quot;.</EmptyMessage>
      )}

      {!hasSearch && currentFolderItems.length === 0 && currentDocuments.length === 0 && currentPath.length > 0 && (
        <EmptyMessage>{documentsLoading ? "Indlæser indhold..." : "Intet indhold endnu."}</EmptyMessage>
      )}

      {!hasSearch && currentFolderItems.length === 0 && currentDocuments.length === 0 && currentPath.length === 0 && currentChildren.length === 0 && (
        <EmptyMessage>{documentsLoading ? "Indlæser indhold..." : "Intet indhold endnu."}</EmptyMessage>
      )}

      {!hasSearch && currentFolderItems.map((item) => {
        const desc = item.description?.trim() ?? "";
        return (
          <FolderContainer key={item.path.join("/")}>
            <FolderRow>
              <FolderMain type="button" onClick={() => handleFolderClick(item.path)}>
                <FolderIcon aria-hidden>
                  <FluentEmojiFileFolderIcon size={36} />
                </FolderIcon>
                <FolderTextCol>
                  <FolderNameText>{item.name}</FolderNameText>
                  <FolderDescriptionText>{desc || "\u00a0"}</FolderDescriptionText>
                </FolderTextCol>
              </FolderMain>
              {categoryType === "custom" && (
                <FolderActions>
                  <FolderIconButton
                    type="button"
                    title="Rediger mappe"
                    aria-label="Rediger mappe"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditFolder?.({
                        nodeId: item.id,
                        name: item.name,
                        description: item.description?.trim() ?? "",
                      });
                    }}
                  >
                    <LucideEdit3Icon />
                  </FolderIconButton>
                  <FolderIconButton
                    type="button"
                    title="Slet mappe"
                    aria-label="Slet mappe"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteFolder?.({
                        nodeId: item.id,
                        name: item.name,
                      });
                    }}
                  >
                    <LucideTrashIcon />
                  </FolderIconButton>
                </FolderActions>
              )}
            </FolderRow>
          </FolderContainer>
        );
      })}

      {!hasSearch && currentDocuments.length > 0 && (
        <DocsListBlock>{renderDocumentList(currentDocuments)}</DocsListBlock>
      )}

      {canAddFolder && onAddFolder && currentNode && (
        <ActionsRow>
          <StyledButton onClick={() => onAddFolder(currentNode.id, currentNode.name || categoryName)}>
            + Tilføj mappe
          </StyledButton>
        </ActionsRow>
      )}
    </div>
  );
}
